<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WhatsappInteractiveMessageTemplatesAPI from 'dashboard/api/whatsappInteractiveMessageTemplates';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CurrencyInput from 'dashboard/components-next/input/CurrencyInput.vue';
import { validPixCode, validPixAmount } from './pixPayment';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';
import AiTemplateDraft from 'dashboard/components-next/whatsapp/AiTemplateDraft.vue';
import { existingTemplateDraft } from 'dashboard/helper/aiTemplateDraft';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  purpose: {
    type: String,
    default: 'send',
    validator: value => ['send', 'template'].includes(value),
  },
  inboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['send', 'save', 'close']);
const { t } = useI18n();
const dialogRef = ref(null);
const activeSection = ref('setup');
const aiGenerationSession = ref(0);
// A configuração da ação fica na mesma etapa do seletor de tipo: ao escolher
// uma modalidade, os campos aparecem imediatamente. WhatsApp Flow não é mais
// oferecido para novas mensagens (Flows são enviados pela etapa do Funil);
// mensagens e modelos legados do tipo Flow continuam editáveis e enviáveis.
const editorSections = computed(() => [
  { key: 'setup', label: t('CONVERSATION.INTERACTIVE.ACTION_TITLE') },
  { key: 'content', label: t('CONVERSATION.INTERACTIVE.CONTENT_TITLE') },
]);
const templateName = ref('');
const editingTemplateId = ref(null);
const selectedTemplateId = ref('');
const savedTemplates = ref([]);
const isLoadingTemplates = ref(false);

const defaultForm = () => ({
  type: 'button',
  referenceId: '',
  pixAmount: '',
  pixDescription: '',
  pixCode: '',
  pixUrl: '',
  merchantName: '',
  pixKey: '',
  pixKeyType: 'CNPJ',
  body: '',
  headerType: '',
  headerText: '',
  headerMediaUrl: '',
  headerFilename: '',
  footerText: '',
  buttons: [{ title: '' }],
  ctaDisplayText: '',
  ctaUrl: '',
  flowId: '',
  flowToken: '',
  flowCta: '',
  flowAction: 'navigate',
  flowScreen: '',
  flowData: '',
  flowMode: 'published',
  catalogThumbnailProductRetailerId: '',
});

const form = reactive(defaultForm());
const isTemplatePurpose = computed(() => props.purpose === 'template');

const dialogTitle = computed(() => {
  if (!isTemplatePurpose.value) return t('CONVERSATION.INTERACTIVE.TITLE');
  return editingTemplateId.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.EDIT_TITLE')
    : t('CONVERSATION.INTERACTIVE.TEMPLATES.NEW_TITLE');
});

const dialogDescription = computed(() =>
  isTemplatePurpose.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.FORM_DESCRIPTION')
    : t('CONVERSATION.INTERACTIVE.DESCRIPTION')
);

const confirmLabel = computed(() =>
  isTemplatePurpose.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.SAVE')
    : t('CONVERSATION.INTERACTIVE.SEND')
);

const savedTemplateOptions = computed(() => [
  {
    value: '',
    label: t('CONVERSATION.INTERACTIVE.TEMPLATES.EMPTY_SELECTION'),
  },
  ...savedTemplates.value.map(template => ({
    value: String(template.id),
    label: template.name,
  })),
]);

const typeOptions = computed(() => [
  { value: 'order_details', label: t('CONVERSATION.INTERACTIVE.PIX.TITLE') },
  { value: 'button', label: t('CONVERSATION.INTERACTIVE.TYPES.BUTTON') },
  { value: 'cta_url', label: t('CONVERSATION.INTERACTIVE.TYPES.CTA_URL') },
  {
    value: 'location_request_message',
    label: t('CONVERSATION.INTERACTIVE.TYPES.LOCATION'),
  },
  ...(form.type === 'flow'
    ? [{ value: 'flow', label: t('CONVERSATION.INTERACTIVE.TYPES.FLOW') }]
    : []),
  {
    value: 'catalog_message',
    label: t('CONVERSATION.INTERACTIVE.TYPES.CATALOG'),
  },
]);

const headerOptions = computed(() => [
  { value: '', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.NONE') },
  { value: 'text', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.TEXT') },
  { value: 'image', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.IMAGE') },
  { value: 'video', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.VIDEO') },
  {
    value: 'document',
    label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.DOCUMENT'),
  },
]);

const flowActionOptions = computed(() => [
  { value: 'navigate', label: t('CONVERSATION.INTERACTIVE.FLOW.NAVIGATE') },
  {
    value: 'data_exchange',
    label: t('CONVERSATION.INTERACTIVE.FLOW.DATA_EXCHANGE'),
  },
]);

const whatsappFlows = ref([]);
const isLoadingFlows = ref(false);
const flowsLoaded = ref(false);

const flowModeOptions = computed(() => [
  {
    value: 'published',
    label: t('CONVERSATION.INTERACTIVE.FLOW.MODE_PUBLISHED'),
  },
  { value: 'draft', label: t('CONVERSATION.INTERACTIVE.FLOW.MODE_DRAFT') },
]);

const selectableFlows = computed(() =>
  whatsappFlows.value.filter(flow =>
    ['PUBLISHED', 'DRAFT'].includes(flow.status)
  )
);

const flowOptions = computed(() =>
  selectableFlows.value.map(flow => ({
    value: flow.meta_flow_id,
    label: [
      flow.name,
      props.inboxId ? null : flow.inbox_name,
      t(`WHATSAPP_FLOWS.STATUS.${flow.status}`),
    ]
      .filter(Boolean)
      .join(' · '),
  }))
);

const selectedFlow = computed(() =>
  whatsappFlows.value.find(flow => flow.meta_flow_id === form.flowId)
);

const flowOutsideInbox = computed(
  () =>
    !!props.inboxId &&
    flowsLoaded.value &&
    !!form.flowId.trim() &&
    !selectedFlow.value
);

const generateFlowToken = () =>
  `talkvia_${crypto.randomUUID().replaceAll('-', '')}`;

const loadWhatsappFlows = async () => {
  isLoadingFlows.value = true;
  try {
    const params = props.inboxId ? { inbox_id: props.inboxId } : {};
    const { data } = await WhatsappFlowsAPI.list(params);
    whatsappFlows.value = data.payload || [];
  } catch {
    whatsappFlows.value = [];
  } finally {
    isLoadingFlows.value = false;
    flowsLoaded.value = true;
  }
};

const selectFlow = metaFlowId => {
  form.flowId = String(metaFlowId || '');
  const flow = selectedFlow.value;
  if (!flow) return;
  form.flowAction = 'navigate';
  form.flowScreen = flow.first_screen_id || form.flowScreen;
  form.flowMode = flow.status === 'DRAFT' ? 'draft' : 'published';
  if (!form.flowToken.trim()) form.flowToken = generateFlowToken();
};

const createFlowUrl = computed(() => {
  const accountId = window.location.pathname.split('/')[3];
  const query = { new: '1' };
  if (props.inboxId) query.inbox_id = props.inboxId;
  return frontendURL(`accounts/${accountId}/campanhas/whatsapp-flows`, query);
});

const openFlowCreation = () => {
  window.open(createFlowUrl.value, '_blank', 'noopener,noreferrer');
};

watch(
  () => form.type,
  type => {
    if (type !== 'flow') return;
    if (!form.flowToken.trim()) form.flowToken = generateFlowToken();
    if (!flowsLoaded.value && !isLoadingFlows.value) loadWhatsappFlows();
  }
);

const flowDataError = computed(() => {
  if (!form.flowData.trim()) return '';
  try {
    const value = JSON.parse(form.flowData);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? ''
      : t('CONVERSATION.INTERACTIVE.ERRORS.FLOW_DATA');
  } catch {
    return t('CONVERSATION.INTERACTIVE.ERRORS.FLOW_DATA');
  }
});

const hasValidHeader = computed(() => {
  if (!form.headerType) return true;
  if (form.headerType === 'text') return !!form.headerText.trim();
  return /^https?:\/\//i.test(form.headerMediaUrl.trim());
});

const pixKeyOptions = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'].map(value => ({
  value,
  label: value === 'EVP' ? 'Chave aleatória' : value,
}));
watch(
  () => form.type,
  type => {
    if (type === 'order_details' && !form.referenceId)
      form.referenceId = crypto.randomUUID().replaceAll('-', '');
  }
);
const hasValidAction = computed(() => {
  if (form.type === 'order_details') {
    const receiverValid =
      !!form.pixDescription.trim() &&
      !!form.merchantName.trim() &&
      !!form.pixKey.trim();
    return (
      receiverValid &&
      (isTemplatePurpose.value ||
        (validPixAmount(form.pixAmount) &&
          validPixCode(form.pixCode.trim()) &&
          /^[a-zA-Z0-9_-]{1,35}$/.test(form.referenceId)))
    );
  }
  if (form.type === 'button') {
    const buttonTitles = form.buttons.map(button => button.title.trim());
    return (
      form.buttons.length >= 1 &&
      form.buttons.length <= 3 &&
      buttonTitles.every(Boolean) &&
      new Set(buttonTitles).size === buttonTitles.length
    );
  }
  if (form.type === 'cta_url') {
    return (
      !!form.ctaDisplayText.trim() && /^https?:\/\//i.test(form.ctaUrl.trim())
    );
  }
  if (form.type === 'flow') {
    return (
      !!form.flowId.trim() &&
      !!form.flowToken.trim() &&
      !!form.flowCta.trim() &&
      (form.flowAction !== 'navigate' || !!form.flowScreen.trim()) &&
      !flowDataError.value
    );
  }
  return true;
});

const isValid = computed(
  () =>
    (!isTemplatePurpose.value || !!templateName.value.trim()) &&
    !!form.body.trim() &&
    form.body.length <= 1024 &&
    form.footerText.length <= 60 &&
    hasValidHeader.value &&
    hasValidAction.value
);

const actionLabel = computed(() => {
  if (form.type === 'order_details')
    return t('CONVERSATION.INTERACTIVE.PIX.COPY');
  if (form.type === 'cta_url') return form.ctaDisplayText;
  if (form.type === 'location_request_message') {
    return t('CONVERSATION.INTERACTIVE.PREVIEW.SEND_LOCATION');
  }
  if (form.type === 'flow') return form.flowCta;
  if (form.type === 'catalog_message') {
    return t('CONVERSATION.INTERACTIVE.PREVIEW.VIEW_CATALOG');
  }
  return '';
});

const reset = () => {
  aiGenerationSession.value += 1;
  Object.assign(form, defaultForm());
  templateName.value = '';
  editingTemplateId.value = null;
  selectedTemplateId.value = '';
};

const applyTemplate = template => {
  if (!template) return;

  const interactive = template.interactive || {};
  templateName.value = template.name || '';
  editingTemplateId.value = template.id || null;
  Object.assign(form, {
    ...defaultForm(),
    type: interactive.type || 'button',
    referenceId: interactive.reference_id || '',
    pixAmount: interactive.amount || '',
    pixDescription: interactive.description || '',
    pixCode: interactive.pix_code || '',
    pixUrl: interactive.pix_url || '',
    merchantName: interactive.merchant_name || '',
    pixKey: interactive.pix_key || '',
    pixKeyType: interactive.pix_key_type || 'CNPJ',
    body: template.content || '',
    headerType: interactive.header_type || '',
    headerText: interactive.header_text || '',
    headerMediaUrl: interactive.header_media_url || '',
    headerFilename: interactive.header_filename || '',
    footerText: interactive.footer_text || '',
    buttons:
      interactive.type === 'button'
        ? (interactive.buttons || []).map(button => ({
            title: button.title || button.id || '',
          }))
        : defaultForm().buttons,
    ctaDisplayText: interactive.cta_display_text || '',
    ctaUrl: interactive.cta_url || '',
    flowId: interactive.flow_id || '',
    flowToken: interactive.flow_token || '',
    flowCta: interactive.flow_cta || '',
    flowAction: interactive.flow_action || 'navigate',
    flowScreen: interactive.flow_screen || '',
    flowData: interactive.flow_data
      ? JSON.stringify(interactive.flow_data, null, 2)
      : '',
    flowMode: interactive.flow_mode === 'draft' ? 'draft' : 'published',
    catalogThumbnailProductRetailerId:
      interactive.catalog_thumbnail_product_retailer_id || '',
  });
};

const aiExistingDraft = computed(() =>
  existingTemplateDraft({
    name: templateName.value,
    body: form.body,
    header: form.headerType === 'text' ? form.headerText : '',
    footer: form.footerText,
    button_texts:
      form.type === 'button' ? form.buttons.map(button => button.title) : [],
    action_text:
      { cta_url: form.ctaDisplayText, flow: form.flowCta }[form.type] || '',
  })
);

const generateAiDraft = options =>
  WhatsappInteractiveMessageTemplatesAPI.generateTemplate(options);
const applyAiDraft = draft => {
  templateName.value = draft.name;
  form.body = draft.body;
  form.footerText = draft.footer;
  if (form.headerType === 'text') form.headerText = draft.header;
  if (form.type === 'button')
    form.buttons = draft.button_texts.map(title => ({ title }));
  if (form.type === 'cta_url' && draft.action_text)
    form.ctaDisplayText = draft.action_text;
  if (form.type === 'flow' && draft.action_text)
    form.flowCta = draft.action_text;
  activeSection.value = 'content';
};

const fetchSavedTemplates = async () => {
  if (isTemplatePurpose.value) return;

  isLoadingTemplates.value = true;
  try {
    const { data } = await WhatsappInteractiveMessageTemplatesAPI.get();
    savedTemplates.value = data;
  } catch {
    savedTemplates.value = [];
  } finally {
    isLoadingTemplates.value = false;
  }
};

watch(selectedTemplateId, value => {
  if (!value) return;
  const template = savedTemplates.value.find(
    item => String(item.id) === String(value)
  );
  applyTemplate(template);
  editingTemplateId.value = null;
});

const open = template => {
  reset();
  flowsLoaded.value = false;
  whatsappFlows.value = [];
  activeSection.value = template ? 'content' : 'setup';
  if (template) applyTemplate(template);
  if (form.type === 'flow') loadWhatsappFlows();
  fetchSavedTemplates();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleClose = () => emit('close');

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ title: '' });
};

const removeButton = index => {
  if (form.buttons.length === 1) return;
  form.buttons.splice(index, 1);
};

const buildInteractiveAttributes = () => {
  const interactive = {
    type: form.type,
    header_type: form.headerType || undefined,
    header_text:
      form.headerType === 'text' ? form.headerText.trim() : undefined,
    header_media_url:
      form.headerType && form.headerType !== 'text'
        ? form.headerMediaUrl.trim()
        : undefined,
    header_filename:
      form.headerType === 'document'
        ? form.headerFilename.trim() || undefined
        : undefined,
    footer_text: form.footerText.trim() || undefined,
  };

  if (form.type === 'order_details') {
    Object.assign(interactive, {
      ...(!isTemplatePurpose.value
        ? {
            reference_id: form.referenceId,
            amount: form.pixAmount,
            pix_code: form.pixCode.trim(),
            pix_url: form.pixUrl.trim() || undefined,
          }
        : {}),
      description: form.pixDescription.trim(),
      merchant_name: form.merchantName.trim(),
      pix_key: form.pixKey.trim(),
      pix_key_type: form.pixKeyType,
    });
  } else if (form.type === 'button') {
    interactive.buttons = form.buttons.map(button => ({
      id: button.title.trim(),
      title: button.title.trim(),
    }));
  } else if (form.type === 'cta_url') {
    interactive.cta_display_text = form.ctaDisplayText.trim();
    interactive.cta_url = form.ctaUrl.trim();
  } else if (form.type === 'flow') {
    interactive.flow_id = form.flowId.trim();
    interactive.flow_token = form.flowToken.trim();
    interactive.flow_cta = form.flowCta.trim();
    interactive.flow_action = form.flowAction;
    interactive.flow_screen = form.flowScreen.trim() || undefined;
    interactive.flow_data = form.flowData.trim()
      ? JSON.parse(form.flowData)
      : undefined;
    interactive.flow_mode = form.flowMode === 'draft' ? 'draft' : undefined;
  } else if (form.type === 'catalog_message') {
    interactive.catalog_thumbnail_product_retailer_id =
      form.catalogThumbnailProductRetailerId.trim() || undefined;
  }

  return interactive;
};

const handleConfirm = () => {
  if (!isValid.value || props.isLoading) return;
  const interactive = buildInteractiveAttributes();

  if (isTemplatePurpose.value) {
    emit('save', {
      id: editingTemplateId.value,
      name: templateName.value.trim(),
      content: form.body.trim(),
      interactive,
    });
    return;
  }

  emit('send', {
    message: form.body.trim(),
    private: false,
    contentType: 'whatsapp_interactive',
    contentAttributes: { interactive },
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="5xl"
    position="top"
    overflow-y-auto
    sticky-header-footer
    :close-on-click-outside="false"
    :title="dialogTitle"
    :description="dialogDescription"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="!isValid"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <nav
      class="mb-4 flex gap-2 overflow-x-auto border-b border-n-weak pb-3"
      :aria-label="dialogTitle"
    >
      <button
        v-for="(section, index) in editorSections"
        :key="section.key"
        type="button"
        class="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
        :class="
          activeSection === section.key
            ? 'bg-n-brand/10 text-n-brand'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
        :aria-current="activeSection === section.key ? 'step' : undefined"
        @click="activeSection = section.key"
      >
        <span
          class="grid size-5 place-items-center rounded-full border border-current text-xs"
        >
          {{ index + 1 }}
        </span>
        <span>{{ section.label }}</span>
      </button>
    </nav>
    <div class="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div class="flex min-w-0 flex-col gap-5">
        <AiTemplateDraft
          v-if="isTemplatePurpose"
          :key="aiGenerationSession"
          :generate="generateAiDraft"
          :context="{
            type: form.type,
            language: 'pt_BR',
            header_format: form.headerType,
            existing_draft: aiExistingDraft,
          }"
          :disabled="isLoading"
          @apply="applyAiDraft"
        />
        <section
          v-if="isTemplatePurpose"
          v-show="activeSection === 'setup'"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <NextInput
            v-model="templateName"
            :label="t('CONVERSATION.INTERACTIVE.TEMPLATES.NAME')"
            :placeholder="
              t('CONVERSATION.INTERACTIVE.TEMPLATES.NAME_PLACEHOLDER')
            "
            maxlength="100"
          />
        </section>

        <section
          v-else-if="savedTemplates.length || isLoadingTemplates"
          v-show="activeSection === 'setup'"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.USE_TEMPLATE') }}
            <NextSelect
              v-model="selectedTemplateId"
              :options="savedTemplateOptions"
              :disabled="isLoadingTemplates"
              class="w-full [&>select]:w-full"
            />
          </label>
          <p class="mb-0 mt-2 text-xs text-n-slate-11">
            {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.USE_TEMPLATE_HELP') }}
          </p>
        </section>

        <section
          v-show="activeSection === 'setup'"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <div class="mb-4 flex items-start gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-mouse-pointer-click size-4" />
            </span>
            <div>
              <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{ t('CONVERSATION.INTERACTIVE.ACTION_TITLE') }}
              </h4>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('CONVERSATION.INTERACTIVE.ACTION_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.TYPE') }}
            <NextSelect
              v-model="form.type"
              :options="typeOptions"
              class="w-full [&>select]:w-full"
            />
          </label>
        </section>

        <section
          v-show="activeSection === 'content'"
          class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.INTERACTIVE.CONTENT_TITLE') }}
          </h4>
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.HEADER') }}
            <NextSelect
              v-model="form.headerType"
              :options="headerOptions"
              class="w-full [&>select]:w-full"
            />
          </label>
          <NextInput
            v-if="form.headerType === 'text'"
            v-model="form.headerText"
            :label="t('CONVERSATION.INTERACTIVE.HEADER_TEXT')"
            maxlength="60"
          />
          <template v-else-if="form.headerType">
            <NextInput
              v-model="form.headerMediaUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.MEDIA_URL')"
              placeholder="https://"
            />
            <NextInput
              v-if="form.headerType === 'document'"
              v-model="form.headerFilename"
              :label="t('CONVERSATION.INTERACTIVE.FILE_NAME')"
            />
          </template>
          <NextTextArea
            v-model="form.body"
            :label="t('CONVERSATION.INTERACTIVE.BODY')"
            :placeholder="t('CONVERSATION.INTERACTIVE.BODY_PLACEHOLDER')"
            :max-length="1024"
            show-character-count
            resize
          />
          <NextInput
            v-model="form.footerText"
            :label="t('CONVERSATION.INTERACTIVE.FOOTER')"
            :placeholder="t('CONVERSATION.INTERACTIVE.FOOTER_PLACEHOLDER')"
            maxlength="60"
          />
        </section>

        <section
          v-show="activeSection === 'setup'"
          class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          data-testid="interactive-action-configuration"
        >
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.INTERACTIVE.CONFIGURATION_TITLE') }}
          </h4>

          <template v-if="form.type === 'order_details'">
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t(
                  isTemplatePurpose
                    ? 'CONVERSATION.INTERACTIVE.PIX.PRESET_HELP'
                    : 'CONVERSATION.INTERACTIVE.PIX.HELP'
                )
              }}
            </p>
            <CurrencyInput
              v-if="!isTemplatePurpose"
              v-model="form.pixAmount"
              :label="t('CONVERSATION.INTERACTIVE.PIX.AMOUNT')"
              :disabled="isLoading"
            />
            <NextInput
              v-model="form.pixDescription"
              :label="t('CONVERSATION.INTERACTIVE.PIX.DESCRIPTION')"
              maxlength="120"
            />
            <NextInput
              v-if="!isTemplatePurpose"
              v-model="form.referenceId"
              :label="t('CONVERSATION.INTERACTIVE.PIX.REFERENCE')"
              maxlength="35"
            />
            <NextInput
              v-model="form.merchantName"
              :label="t('CONVERSATION.INTERACTIVE.PIX.MERCHANT')"
              maxlength="100"
            />
            <label
              class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CONVERSATION.INTERACTIVE.PIX.KEY_TYPE') }}
              <NextSelect v-model="form.pixKeyType" :options="pixKeyOptions" />
            </label>
            <NextInput
              v-model="form.pixKey"
              :label="t('CONVERSATION.INTERACTIVE.PIX.KEY')"
              maxlength="254"
            />
            <NextInput
              v-if="!isTemplatePurpose"
              v-model="form.pixUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.PIX.URL')"
              placeholder="https://"
              maxlength="2048"
            />
            <NextTextArea
              v-if="!isTemplatePurpose"
              v-model="form.pixCode"
              :label="t('CONVERSATION.INTERACTIVE.PIX.CODE')"
              :max-length="2048"
              :message="
                form.pixCode && !validPixCode(form.pixCode.trim())
                  ? t('CONVERSATION.INTERACTIVE.PIX.INVALID_CODE')
                  : ''
              "
              message-type="error"
              resize
            />
          </template>
          <template v-else-if="form.type === 'button'">
            <div class="flex flex-col gap-2">
              <div
                v-for="(button, index) in form.buttons"
                :key="index"
                class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
              >
                <NextInput
                  v-model="button.title"
                  :label="
                    t('CONVERSATION.INTERACTIVE.BUTTON_TITLE', {
                      number: index + 1,
                    })
                  "
                  maxlength="20"
                />
                <NextButton
                  v-if="form.buttons.length > 1"
                  type="button"
                  color="ruby"
                  variant="ghost"
                  icon="i-lucide-trash-2"
                  :aria-label="t('CONVERSATION.INTERACTIVE.REMOVE_BUTTON')"
                  @click="removeButton(index)"
                />
              </div>
              <NextButton
                v-if="form.buttons.length < 3"
                type="button"
                class="self-start"
                size="sm"
                variant="faded"
                icon="i-lucide-plus"
                :label="t('CONVERSATION.INTERACTIVE.ADD_BUTTON')"
                @click="addButton"
              />
            </div>
          </template>

          <template v-else-if="form.type === 'cta_url'">
            <NextInput
              v-model="form.ctaDisplayText"
              :label="t('CONVERSATION.INTERACTIVE.LINK.TEXT')"
              maxlength="20"
            />
            <NextInput
              v-model="form.ctaUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.LINK.URL')"
              placeholder="https://"
            />
          </template>

          <div
            v-else-if="form.type === 'location_request_message'"
            class="flex items-start gap-3 rounded-lg bg-n-teal-3 p-3 text-n-teal-12"
          >
            <span class="i-lucide-map-pin size-5 shrink-0" />
            <p class="mb-0 text-sm">
              {{ t('CONVERSATION.INTERACTIVE.LOCATION_DESCRIPTION') }}
            </p>
          </div>

          <template v-else-if="form.type === 'flow'">
            <div
              class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
              data-testid="interactive-flow-configuration"
            >
              <div class="flex flex-wrap items-end gap-2">
                <label
                  class="flex min-w-0 flex-1 flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                >
                  {{ t('CONVERSATION.INTERACTIVE.FLOW.SELECT') }}
                  <ComboBox
                    :model-value="form.flowId"
                    :options="flowOptions"
                    :display-label="selectedFlow?.name || form.flowId"
                    :placeholder="
                      t('CONVERSATION.INTERACTIVE.FLOW.SELECT_PLACEHOLDER')
                    "
                    :search-placeholder="
                      t('CONVERSATION.INTERACTIVE.FLOW.SEARCH')
                    "
                    :empty-state="t('CONVERSATION.INTERACTIVE.FLOW.EMPTY')"
                    :disabled="isLoadingFlows"
                    class="w-full"
                    @update:model-value="selectFlow"
                  />
                </label>
                <NextButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  icon="i-hugeicons-refresh"
                  :is-loading="isLoadingFlows"
                  :aria-label="t('CONVERSATION.INTERACTIVE.FLOW.REFRESH')"
                  :title="t('CONVERSATION.INTERACTIVE.FLOW.REFRESH')"
                  @click="loadWhatsappFlows"
                />
                <NextButton
                  type="button"
                  size="sm"
                  variant="faded"
                  icon="i-hugeicons-add-01"
                  :label="t('CONVERSATION.INTERACTIVE.FLOW.CREATE')"
                  @click="openFlowCreation"
                />
              </div>
              <p class="mb-0 text-xs text-n-slate-11">
                {{
                  flowsLoaded && !selectableFlows.length
                    ? t('CONVERSATION.INTERACTIVE.FLOW.EMPTY_HELP')
                    : t('CONVERSATION.INTERACTIVE.FLOW.SELECT_HELP')
                }}
              </p>
              <p
                v-if="form.flowMode === 'draft'"
                class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-2 p-2 text-xs text-n-amber-12"
              >
                <span class="i-hugeicons-alert-circle size-4 shrink-0" />
                {{ t('CONVERSATION.INTERACTIVE.FLOW.DRAFT_HINT') }}
              </p>
              <p
                v-if="flowOutsideInbox"
                class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-2 p-2 text-xs text-n-amber-12"
              >
                <span class="i-hugeicons-alert-circle size-4 shrink-0" />
                {{ t('CONVERSATION.INTERACTIVE.FLOW.NOT_IN_INBOX') }}
              </p>
            </div>
            <NextInput
              v-model="form.flowCta"
              :label="t('CONVERSATION.INTERACTIVE.FLOW.CTA')"
              :placeholder="t('CONVERSATION.INTERACTIVE.FLOW.CTA_PLACEHOLDER')"
              maxlength="20"
            />
            <details class="rounded-xl border border-n-weak p-3">
              <summary
                class="cursor-pointer text-sm font-medium text-n-slate-12"
              >
                {{ t('CONVERSATION.INTERACTIVE.FLOW.ADVANCED') }}
              </summary>
              <div class="mt-3 flex flex-col gap-4">
                <div class="grid gap-4 sm:grid-cols-2">
                  <NextInput
                    v-model="form.flowId"
                    :label="t('CONVERSATION.INTERACTIVE.FLOW.ID')"
                    :message="t('CONVERSATION.INTERACTIVE.FLOW.MANUAL_ID_HELP')"
                  />
                  <NextInput
                    v-model="form.flowToken"
                    :label="t('CONVERSATION.INTERACTIVE.FLOW.TOKEN')"
                    :message="t('CONVERSATION.INTERACTIVE.FLOW.TOKEN_HELP')"
                  />
                  <label
                    class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CONVERSATION.INTERACTIVE.FLOW.ACTION') }}
                    <NextSelect
                      v-model="form.flowAction"
                      :options="flowActionOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <label
                    class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CONVERSATION.INTERACTIVE.FLOW.MODE') }}
                    <NextSelect
                      v-model="form.flowMode"
                      :options="flowModeOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <NextInput
                    v-if="form.flowAction === 'navigate'"
                    v-model="form.flowScreen"
                    :label="t('CONVERSATION.INTERACTIVE.FLOW.SCREEN')"
                    :message="
                      form.flowScreen.trim()
                        ? ''
                        : t('CONVERSATION.INTERACTIVE.FLOW.SCREEN_REQUIRED')
                    "
                    :message-type="form.flowScreen.trim() ? 'info' : 'error'"
                  />
                </div>
                <NextTextArea
                  v-model="form.flowData"
                  :label="t('CONVERSATION.INTERACTIVE.FLOW.DATA')"
                  :placeholder="
                    t('CONVERSATION.INTERACTIVE.FLOW.DATA_PLACEHOLDER')
                  "
                  :message="flowDataError"
                  :message-type="flowDataError ? 'error' : 'info'"
                  resize
                />
              </div>
            </details>
          </template>

          <template v-else-if="form.type === 'catalog_message'">
            <NextInput
              v-model="form.catalogThumbnailProductRetailerId"
              :label="t('CONVERSATION.INTERACTIVE.CATALOG.PRODUCT_ID')"
              :message="t('CONVERSATION.INTERACTIVE.CATALOG.PRODUCT_HELP')"
            />
          </template>
        </section>
      </div>

      <aside class="lg:sticky lg:top-0 lg:self-start">
        <p
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('CONVERSATION.INTERACTIVE.PREVIEW.TITLE') }}
        </p>
        <div class="rounded-2xl bg-[#efeae2] p-4 dark:bg-n-solid-2">
          <div
            class="overflow-hidden rounded-xl bg-white text-[#334155] shadow-sm"
          >
            <div
              v-if="form.headerType"
              class="flex min-h-16 items-center justify-center bg-[#f1f5f9] px-4 py-3 text-sm font-semibold"
            >
              <span v-if="form.headerType === 'text'">
                {{
                  form.headerText ||
                  t('CONVERSATION.INTERACTIVE.PREVIEW.HEADER')
                }}
              </span>
              <span v-else class="flex items-center gap-2 text-[#64748b]">
                <span class="i-lucide-paperclip size-4" />
                {{
                  headerOptions.find(option => option.value === form.headerType)
                    ?.label
                }}
              </span>
            </div>
            <div class="px-4 py-3">
              <p class="mb-0 whitespace-pre-wrap text-sm">
                {{ form.body || t('CONVERSATION.INTERACTIVE.PREVIEW.BODY') }}
              </p>
              <p
                v-if="form.footerText"
                class="mb-0 mt-2 text-xs text-[#64748b]"
              >
                {{ form.footerText }}
              </p>
            </div>
            <div
              v-if="form.type === 'order_details'"
              class="border-t border-[#e2e8f0] px-4 py-3"
            >
              <p class="mb-1 text-xs">{{ form.pixDescription }}</p>
              <strong class="text-lg">{{
                isTemplatePurpose
                  ? t('CONVERSATION.INTERACTIVE.PIX.AMOUNT_ON_SEND')
                  : new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(Number(form.pixAmount) || 0)
              }}</strong>
              <p class="mb-0 mt-1 text-xs">{{ form.merchantName }}</p>
            </div>
            <div
              v-if="form.type === 'button'"
              class="border-t border-[#e2e8f0]"
            >
              <div
                v-for="(button, index) in form.buttons"
                :key="`${button.title}-${index}`"
                class="flex min-h-10 items-center justify-center border-b border-[#e2e8f0] px-3 text-sm font-medium text-[#0284c7] last:border-b-0"
              >
                {{
                  button.title || t('CONVERSATION.INTERACTIVE.PREVIEW.BUTTON')
                }}
              </div>
            </div>
            <div
              v-else
              class="flex min-h-11 items-center justify-center gap-2 border-t border-[#e2e8f0] px-3 text-sm font-medium text-[#0284c7]"
            >
              <span
                :class="{
                  'i-hugeicons-money-receive-01': form.type === 'order_details',
                  'i-lucide-external-link': form.type === 'cta_url',
                  'i-lucide-map-pin': form.type === 'location_request_message',
                  'i-lucide-list-checks': form.type === 'flow',
                  'i-lucide-shopping-bag': form.type === 'catalog_message',
                }"
                class="size-4"
              />
              {{ actionLabel || t('CONVERSATION.INTERACTIVE.PREVIEW.ACTION') }}
            </div>
          </div>
        </div>
        <p class="mb-0 mt-3 text-xs text-n-slate-11">
          {{ t('CONVERSATION.INTERACTIVE.WINDOW_NOTICE') }}
        </p>
      </aside>
    </div>
  </Dialog>
</template>
